// #295 — the live-alert chime. Browsers may keep audio blocked until the user has touched the page, so:
// one shared AudioContext, created when the app starts (already on where the browser allows it) and
// otherwise unlocked (resume) by a gesture that grants user activation; the listeners stay until it
// really runs (a gesture that did not unlock it is not the last chance) and come back if the browser
// suspends it later (iOS: background, a call). Readiness is observable, so the header can say "sound
// off" instead of failing silently. Nothing here throws: a browser without Web Audio simply never plays.

import type { CriticalSoundKind } from '@/types'

export type { CriticalSoundKind }

type Ctx = AudioContext

let ctx: Ctx | null = null
let lastPlayedAt = -Infinity
// #294 redesign — the user's chime switch and volume (Settings → Sounds & display, saved per browser):
// the display prefs store passes them in on load and on every change, so every chime follows them.
let chimeEnabled = true
let chimeVolume = 1
// The gain buses of the critical plays still sounding: stopCriticalSound fades them out.
const liveBuses = new Set<GainNode>()
const listeners = new Set<() => void>()
// Whether the app asked for gesture listening (installAlertSoundUnlock), and whether they are on now.
let wanted = false
let armed = false

// Three rising notes, ~0.9 s from the first note to the end of the last, peak gain 0.35.
const NOTES = [660, 880, 1175]
const NOTE_GAP_S = 0.22
const NOTE_LEN_S = 0.45
const PEAK_GAIN = 0.35
// A burst of alerts plays one chime.
const MIN_GAP_MS = 1500
// #294 redesign — the volume floors: the chime may go down to 10 %, a critical sound never below 30 %
// (so a critical alert is never silent). Anything that is not a number (bad storage) plays at 100 %.
const CHIME_MIN_VOLUME = 0.1
const CRITICAL_MIN_VOLUME = 0.3

function clampVolume(volume: number, min: number): number {
  if (typeof volume !== 'number' || !Number.isFinite(volume)) return 1
  return Math.min(1, Math.max(min, volume))
}

function ctor(): (new () => Ctx) | undefined {
  if (typeof window === 'undefined') return undefined
  const w = window as unknown as { AudioContext?: new () => Ctx; webkitAudioContext?: new () => Ctx }
  return w.AudioContext ?? w.webkitAudioContext
}

function notify() { listeners.forEach((l) => l()) }

function context(): Ctx | null {
  if (ctx) return ctx
  const C = ctor()
  if (!C) return null
  try {
    ctx = new C()
    // A browser may suspend it again later: the header follows, and the next gesture can unlock it.
    ctx.onstatechange = () => {
      if (ctx && ctx.state !== 'running' && wanted) arm()
      // Some browsers start an allowed context a moment after creating it: no gesture needed then.
      if (ctx && ctx.state === 'running') disarm()
      notify()
    }
  } catch {
    ctx = null
  }
  return ctx
}

export function isAlertSoundSupported(): boolean {
  return !!ctor()
}

export function isAlertSoundReady(): boolean {
  return ctx?.state === 'running'
}

export function subscribeAlertSound(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

/** Try to start the audio (call from a user gesture). Resolves whether it now runs. */
export async function unlockAlertSound(): Promise<boolean> {
  const c = context()
  if (!c) return false
  try {
    if (c.state !== 'running') await c.resume()
  } catch {
    // still blocked; the next gesture tries again
  }
  notify()
  return isAlertSoundReady()
}

// The gestures that grant user activation (HTML spec): keydown, pointerup of a non-mouse pointer,
// touchend and click; pointerdown is a mouse's activation. A touch's pointerdown / touchstart is not.
const GESTURES = ['pointerdown', 'pointerup', 'touchend', 'click', 'keydown'] as const
// The header's "sound off" button carries this attribute: a gesture that starts on it also plays the
// test chime here, because the button is gone (sound on) before its own click arrives.
const BUTTON_ATTR = 'data-alert-sound-button'

function onGesture(e: Event) {
  const onButton = e.target instanceof Element && !!e.target.closest(`[${BUTTON_ATTR}]`)
  void unlockAlertSound().then((ready) => {
    if (!ready) return
    disarm()
    if (onButton) playAlertSound()
  })
}

function arm() {
  if (armed || typeof window === 'undefined') return
  armed = true
  GESTURES.forEach((g) => window.addEventListener(g, onGesture, true))
}

function disarm() {
  if (!armed) return
  armed = false
  GESTURES.forEach((g) => window.removeEventListener(g, onGesture, true))
}

/** Listen for gestures until the sound runs, and again whenever the browser suspends it. Calling it
 * again while listening adds nothing. Returns a cleanup. */
export function installAlertSoundUnlock(): () => void {
  if (typeof window === 'undefined' || !isAlertSoundSupported()) return () => {}
  wanted = true
  // Ask the browser now instead of assuming it is blocked: where it already allows sound (Chrome after
  // a reload of a page the user clicked, a site set to allow sound), the new context starts running and
  // no gesture is needed. Where it does not, the context waits suspended for the first gesture.
  context()
  if (!isAlertSoundReady()) arm()
  notify()
  return () => { wanted = false; disarm() }
}

export const ALERT_SOUND_BUTTON_ATTR = BUTTON_ATTR

/** The user's chime switch (Settings → Sounds & display). Off = no live chime and no header test chime. */
export function setChimeEnabled(on: boolean): void {
  chimeEnabled = on !== false
}

/** The user's chime volume as a fraction (0.1–1; the store's 10–100 % divided by 100). */
export function setChimeVolume(volume: number): void {
  chimeVolume = clampVolume(volume, CHIME_MIN_VOLUME)
}

function chime(c: Ctx, volume: number): boolean {
  try {
    const peak = PEAK_GAIN * clampVolume(volume, CHIME_MIN_VOLUME)
    const t0 = c.currentTime
    NOTES.forEach((freq, i) => {
      const osc = c.createOscillator()
      const gain = c.createGain()
      osc.connect(gain)
      gain.connect(c.destination)
      osc.type = 'sine'
      osc.frequency.value = freq
      const start = t0 + i * NOTE_GAP_S
      gain.gain.setValueAtTime(0, start)
      gain.gain.linearRampToValueAtTime(peak, start + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.001, start + NOTE_LEN_S)
      osc.start(start)
      osc.stop(start + NOTE_LEN_S)
    })
    return true
  } catch {
    return false
  }
}

/** Play the chime if the browser lets us and the user has not switched it off: at `volume` (0.1–1,
 * clamped), else at the user's chime volume; at most one per 1.5 s. Returns whether it played. */
export function playAlertSound(volume?: number): boolean {
  if (!chimeEnabled) return false
  const c = context()
  if (!c || c.state !== 'running') return false
  const nowMs = Date.now()
  if (nowMs - lastPlayedAt < MIN_GAP_MS) return false
  if (!chime(c, volume ?? chimeVolume)) return false
  lastPlayedAt = nowMs
  return true
}

/** Settings' Play button: the chime at `volume` (0.1–1) even when the user switched the live chime off,
 * and outside the burst throttle (it is a test, not an alert). Returns whether it played. */
export function playChimePreview(volume: number): boolean {
  const c = context()
  if (!c || c.state !== 'running') return false
  return chime(c, volume)
}

// #294 — the critical sounds (the owner's picks from the preview, numbers copied from it). Each note
// has a 15 ms attack and a 30 ms release; each play goes through its own gain bus, so stopCriticalSound
// can cut it. How often a sound plays is the repeat controller's job (useCriticalSound), so no throttle.
// "Hazard pattern" (default): ten 0.17 s slots, two of them rests, each note 0.13 s, a triangle wave
// through a 3200 Hz low-pass, peak 0.35 — 1.7 s.
const HAZARD_NOTES: Array<number | null> = [880, 1175, 1397, null, 880, 1175, 1397, null, 1397, 1397]
const HAZARD_STEP_S = 0.17
const HAZARD_NOTE_S = 0.13
const HAZARD_PEAK = 0.35
const HAZARD_LOWPASS_HZ = 3200
// "Two-tone siren": five square notes alternating 960/770 Hz, 0.32 s apart, 0.30 s long, peak 0.09 — 1.58 s.
const SIREN_NOTES = [960, 770, 960, 770, 960]
const SIREN_STEP_S = 0.32
const SIREN_NOTE_S = 0.3
const SIREN_PEAK = 0.09
// "Horn": three blasts 0.5 s apart, each two sawtooth notes together (392 Hz peak 0.08 + 494 Hz peak
// 0.06), 0.36 s long — 1.36 s.
const HORN_BLASTS = 3
const HORN_STEP_S = 0.5
const HORN_NOTE_S = 0.36
const HORN_TONES: Array<[number, number]> = [[392, 0.08], [494, 0.06]]
const ATTACK_S = 0.015
const RELEASE_S = 0.03
const TAIL_S = 0.02
// stopCriticalSound fades a bus to silence over this long (a hard cut would click).
const STOP_FADE_S = 0.015

/** How long each critical sound lasts, in seconds: every synthesized one at most 1.7 s; #338 the client's
 *  recording 5.06 s (the file is 5.064 s). */
// (10 × 0.17; 4 × 0.32 + 0.30; 2 × 0.5 + 0.36 — written out, so floating point cannot push one past 1.7)
export const CRITICAL_SOUND_SECONDS: Record<CriticalSoundKind, number> = { client: 5.06, hazard: 1.7, siren: 1.58, horn: 1.36 }

// #338 — the client's own recording (client request via owner 2026-10-13), the default critical sound: a file,
// not oscillators (mp3, mono 24 kHz, 5.064 s, peak −1.4 dBFS, mean −8.4 dB). Fetched and decoded ONCE: the load
// is shared while it runs and the buffer is kept; a failed load is forgotten, so the next play (or the
// controller's next ask) tries again. It plays through a level gain and a play bus like the synthesized sounds,
// so stopCriticalSound and the user's critical volume apply.
const CLIENT_SOUND_URL = '/sounds/critical-client.mp3'
/** #338 — the recording's level. Its measured peak is −1.4 dBFS ≈ 0.85 linear; 0.85 × 0.41 ≈ 0.35 = HAZARD_PEAK,
 *  the Hazard pattern's peak (the default before #338), at 100 % critical volume. One named constant, so the owner
 *  can tune it after the stage look. */
export const CLIENT_SOUND_GAIN = 0.41
/** #338 review fix round 1 — a load that has not finished after this long is given up (the fetch is aborted), so a
 *  hung request never blocks the retries or a waiting Play click. */
export const CLIENT_SOUND_TIMEOUT_MS = 15_000
/** #338 review fix round 1 — after a failed load nothing fetches the file again for this long (a missing file
 *  would otherwise be fetched and decoded on every fallback round); the Hazard fallback covers those rounds. */
export const CLIENT_SOUND_RETRY_AFTER_MS = 60_000
let clientBuffer: AudioBuffer | null = null
let clientLoad: Promise<boolean> | null = null
// When the last load failed (Date.now()), for the back-off; null = no failure since the last success.
let clientFailedAt: number | null = null
// Bumped by the test reset: a load still on its way from before the reset is dropped.
let clientGeneration = 0
let clientFallbackWarned = false

/** One note: oscillator (→ low-pass) → its envelope gain → `dest` (the play's bus). */
function tone(c: Ctx, dest: AudioNode, type: OscillatorType, freq: number, start: number, length: number, peak: number, lowpassHz?: number) {
  const osc = c.createOscillator()
  const gain = c.createGain()
  osc.type = type
  osc.frequency.value = freq
  if (lowpassHz) {
    const filter = c.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.value = lowpassHz
    osc.connect(filter)
    filter.connect(gain)
  } else {
    osc.connect(gain)
  }
  gain.connect(dest)
  gain.gain.setValueAtTime(0, start)
  gain.gain.linearRampToValueAtTime(peak, start + ATTACK_S)
  gain.gain.setValueAtTime(peak, start + Math.max(ATTACK_S, length - RELEASE_S))
  gain.gain.linearRampToValueAtTime(0.0001, start + length)
  osc.start(start)
  osc.stop(start + length + TAIL_S)
}

/** The notes of a synthesized critical sound on `bus` (an unknown kind = the Hazard pattern). */
function synthesized(c: Ctx, bus: GainNode, kind: CriticalSoundKind, t0: number, vol: number) {
  if (kind === 'siren') {
    SIREN_NOTES.forEach((freq, i) => tone(c, bus, 'square', freq, t0 + i * SIREN_STEP_S, SIREN_NOTE_S, SIREN_PEAK * vol))
  } else if (kind === 'horn') {
    for (let i = 0; i < HORN_BLASTS; i++) {
      HORN_TONES.forEach(([freq, peak]) => tone(c, bus, 'sawtooth', freq, t0 + i * HORN_STEP_S, HORN_NOTE_S, peak * vol))
    }
  } else {
    HAZARD_NOTES.forEach((freq, i) => {
      if (freq !== null) tone(c, bus, 'triangle', freq, t0 + i * HAZARD_STEP_S, HAZARD_NOTE_S, HAZARD_PEAK * vol, HAZARD_LOWPASS_HZ)
    })
  }
}

/** #338 — the recording on `bus`: buffer source → level gain (CLIENT_SOUND_GAIN × volume) → bus. */
function recording(c: Ctx, bus: GainNode, buffer: AudioBuffer, t0: number, vol: number) {
  const src = c.createBufferSource()
  const level = c.createGain()
  src.buffer = buffer
  level.gain.value = CLIENT_SOUND_GAIN * vol
  src.connect(level)
  level.connect(bus)
  src.start(t0)
}

/** One play on its own gain bus to the speakers (so stopCriticalSound can cut it), let go 0.4 s after the sound
 *  has ended. Returns whether it was scheduled; a failure leaves nothing connected. */
function playOnBus(c: Ctx, seconds: number, schedule: (bus: GainNode, t0: number) => void): boolean {
  let bus: GainNode | null = null
  try {
    bus = c.createGain()
    bus.gain.value = 1
    bus.connect(c.destination)
    liveBuses.add(bus)
    schedule(bus, c.currentTime + 0.02)
    const done = bus
    setTimeout(() => retire(done), (seconds + 0.4) * 1000)
    return true
  } catch {
    if (bus) retire(bus)
    return false
  }
}

/** Play a critical sound (default the Hazard pattern; an unknown kind plays it too) at `volume` (0.3–1,
 * clamped: never silent) through the same context and unlock as the chime, if the browser lets us.
 * #338: the client's recording plays from its decoded file; while it is not ready (still loading, or the load
 * failed) or cannot be scheduled, that round plays the Hazard pattern instead (one console warning per page load)
 * and the load is tried again (at most once a minute after a failure) — a critical alert never goes silent.
 * Returns whether it played. */
export function playCriticalSound(kind: CriticalSoundKind = 'hazard', volume = 1): boolean {
  const c = context()
  if (!c || c.state !== 'running') return false
  const vol = clampVolume(volume, CRITICAL_MIN_VOLUME)
  if (kind === 'client') {
    const buffer = clientBuffer
    const seconds = Math.max(CRITICAL_SOUND_SECONDS.client, buffer?.duration || 0)
    if (buffer && playOnBus(c, seconds, (bus, t0) => recording(c, bus, buffer, t0, vol))) return true
    if (!buffer) void prepareCriticalSound('client')
    warnClientFallback()
    return playOnBus(c, CRITICAL_SOUND_SECONDS.hazard, (bus, t0) => synthesized(c, bus, 'hazard', t0, vol))
  }
  const seconds = CRITICAL_SOUND_SECONDS[kind] ?? CRITICAL_SOUND_SECONDS.hazard
  return playOnBus(c, seconds, (bus, t0) => synthesized(c, bus, kind, t0, vol))
}

function warnClientFallback() {
  if (clientFallbackWarned) return
  clientFallbackWarned = true
  if (typeof console !== 'undefined') {
    console.warn('[alertSound] #338 the client\'s critical sound is not ready; playing the Hazard pattern instead')
  }
}

/** decodeAudioData in both forms: the callbacks (older Safari returns no promise) and the returned promise. */
function decode(c: Ctx, data: ArrayBuffer): Promise<AudioBuffer> {
  return new Promise<AudioBuffer>((resolve, reject) => {
    const p = c.decodeAudioData(data, resolve, reject) as Promise<AudioBuffer> | undefined
    if (p && typeof p.then === 'function') p.then(resolve, reject)
  })
}

async function fetchAndDecode(c: Ctx, generation: number, signal: AbortSignal | undefined): Promise<boolean> {
  try {
    const res = await fetch(CLIENT_SOUND_URL, signal ? { signal } : undefined)
    if (!res.ok) return false
    const buffer = await decode(c, await res.arrayBuffer())
    if (generation !== clientGeneration) return false
    clientBuffer = buffer
    return true
  } catch {
    return false   // network, aborted, an answer that is not audio (e.g. the SPA's index.html), no fetch: retried later
  }
}

/** Fetch and decode, given up after CLIENT_SOUND_TIMEOUT_MS: the fetch is aborted, and the race ends the load even
 *  if something ignores the abort (a decode that never answers). */
async function loadClientSound(c: Ctx, generation: number): Promise<boolean> {
  const abort = typeof AbortController === 'function' ? new AbortController() : null
  let timer: ReturnType<typeof setTimeout> | undefined
  const timedOut = new Promise<boolean>((resolve) => {
    timer = setTimeout(() => {
      try { abort?.abort() } catch { /* nothing to abort */ }
      resolve(false)
    }, CLIENT_SOUND_TIMEOUT_MS)
  })
  try {
    return await Promise.race([fetchAndDecode(c, generation, abort?.signal), timedOut])
  } finally {
    clearTimeout(timer)
  }
}

/** Inside the back-off after a failed load (a computer clock that moved back does not count). */
function clientBackingOff(): boolean {
  if (clientFailedAt === null) return false
  const since = Date.now() - clientFailedAt
  return since >= 0 && since < CLIENT_SOUND_RETRY_AFTER_MS
}

/** #338 — get a critical sound ready to play. The client's recording is fetched and decoded once: callers while
 *  it loads share the load, later ones are answered from the cached buffer. A load is given up after 15 s; a
 *  failed one is forgotten, and the next call after CLIENT_SOUND_RETRY_AFTER_MS (60 s) tries again (sooner ones
 *  answer false at once). Decoding needs no gesture, so it works while the browser still blocks sound. The
 *  synthesized sounds are always ready. Resolves whether it is ready; never rejects, never throws. */
export function prepareCriticalSound(kind: CriticalSoundKind): Promise<boolean> {
  if (kind !== 'client' || clientBuffer) return Promise.resolve(true)
  if (clientLoad) return clientLoad
  if (clientBackingOff()) return Promise.resolve(false)
  const c = context()
  if (!c) return Promise.resolve(false)
  const generation = clientGeneration
  const load = loadClientSound(c, generation).then((ok) => {
    if (generation === clientGeneration) {
      clientLoad = null
      clientFailedAt = ok ? null : Date.now()
    }
    return ok
  })
  clientLoad = load
  return load
}

function retire(bus: GainNode) {
  liveBuses.delete(bus)
  try { bus.disconnect() } catch { /* already gone */ }
}

/** Cut every critical sound still playing (a ~15 ms fade): "Quiet 2 min", none open, logout. The chime
 * is not touched. Never throws. */
export function stopCriticalSound(): void {
  if (!ctx || liveBuses.size === 0) return
  const t = ctx.currentTime
  for (const bus of [...liveBuses]) {
    liveBuses.delete(bus)
    try {
      bus.gain.cancelScheduledValues(t)
      bus.gain.setValueAtTime(bus.gain.value, t)
      bus.gain.linearRampToValueAtTime(0, t + STOP_FADE_S)
    } catch {
      retire(bus)
      continue
    }
    setTimeout(() => retire(bus), 100)
  }
}

/** Tests only: forget the shared context and the throttle. */
export function __resetAlertSoundForTests() {
  disarm()
  wanted = false
  ctx = null
  lastPlayedAt = -Infinity
  listeners.clear()
  chimeEnabled = true
  chimeVolume = 1
  liveBuses.clear()
  clientBuffer = null
  clientLoad = null
  clientFailedAt = null
  clientGeneration += 1
  clientFallbackWarned = false
}
